import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import wheelImage from '../../assets/spin-wheel/Spin the Wheel.png';
import strawberry from '../../assets/spin-wheel/Strawberry.png';
import orange from '../../assets/spin-wheel/Orange.png';
import banana from '../../assets/spin-wheel/Banana.png';
import mango from '../../assets/spin-wheel/Mango.png';
import melon from '../../assets/spin-wheel/Melon.png';
import pineapple from '../../assets/spin-wheel/Pineapple.png';
import oneGB from '../../assets/spin-wheel/1 GB.png';
import tryAgain from '../../assets/spin-wheel/Try Again.png';
import oneGBCollectedScreen from '../../assets/spin-wheel/1 GB Collected Screen.png';
import bananaBasketGif from '../../assets/spin-wheel/Kito Basket Banana.gif';
import mangoBasketGif from '../../assets/spin-wheel/Kito Basket Mango.gif';
import melonBasketGif from '../../assets/spin-wheel/Kito Basket Watermelon.gif';
import pineappleBasketGif from '../../assets/spin-wheel/Kito Basket Pineapple.gif';
import strawberryBasketGif from '../../assets/spin-wheel/Kito Basket Strawberry.gif';
import fullBasketGif from '../../assets/spin-wheel/Kito Basket full.gif';
import {
  consumeSpinWheelProgress,
  getRemainingOpenDays,
  isSpinWheelEligible,
  normalizeSpinWheelProgress,
} from './spinWheelProgress';
import { getOrCreateSessionId, loadProgressRecord, saveProgressRecord } from './spinWheelProgressService';
import { SPIN_WHEEL_COMING_SOON } from '../../config/featureFlags';
import './spinWheel.css';

const SECTIONS = [
  { id: 'banana', name: 'Banana', image: banana, type: 'fruit' },
  { id: 'orange', name: 'Orange', image: orange, type: 'fruit' },
  { id: 'try-again', name: 'Try Again', image: tryAgain, type: 'try-again' },
  { id: 'mango', name: 'Mango', image: mango, type: 'fruit' },
  { id: 'one-gb', name: '1 GB', image: oneGB, resultImage: oneGBCollectedScreen, type: 'reward' },
  { id: 'melon', name: 'Melon', image: melon, type: 'fruit' },
  { id: 'strawberry', name: 'Strawberry', image: strawberry, type: 'fruit' },
  { id: 'pineapple', name: 'Pineapple', image: pineapple, type: 'fruit' },
];

const SECTION_ANGLE = 360 / SECTIONS.length; // 45 degrees per section
const SPIN_DURATION_MS = 3000;
const FULL_BASKET_REWARD_DELAY_MS = 2200;
const FULL_SPINS = 5;
const SPIN_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const FRUIT_NAMES = SECTIONS.filter((section) => section.type === 'fruit').map((section) => section.name);
const SPIN_PROGRESS_UNAVAILABLE_MESSAGE = 'We could not check your spin status. Please try again later.';
const FRUIT_RESULT_GIFS = {
  Banana: bananaBasketGif,
  Mango: mangoBasketGif,
  Melon: melonBasketGif,
  Pineapple: pineappleBasketGif,
  Strawberry: strawberryBasketGif,
};
const FRUIT_IMAGES_BY_NAME = SECTIONS.reduce((imagesByName, section) => {
  if (section.type !== 'fruit') {
    return imagesByName;
  }

  return {
    ...imagesByName,
    [section.name]: section.image,
  };
}, {});

const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;

const getValidCollectedFruits = (fruits) => {
  if (!Array.isArray(fruits)) {
    return [];
  }

  return [...new Set(fruits)].filter((fruitName) => FRUIT_NAMES.includes(fruitName));
};

const hasCollectedAllFruits = (collectedFruits) =>
  FRUIT_NAMES.every((fruitName) => collectedFruits.includes(fruitName));

const getTargetSectionIndexes = (collectedFruits) => {
  const allFruitsCollected = hasCollectedAllFruits(collectedFruits);

  return SECTIONS.reduce((indexes, section, index) => {
    if (section.type === 'reward' && !allFruitsCollected) {
      return indexes;
    }

    return [...indexes, index];
  }, []);
};

const getRotationForSectionAtTop = (sectionIndex) => {
  const sectionCenterAngle = sectionIndex * SECTION_ANGLE + SECTION_ANGLE / 2;
  return normalizeAngle(360 - sectionCenterAngle);
};

const getSectionIndexAtTop = (wheelRotation) => {
  const angleAtTopOnWheel = normalizeAngle(360 - normalizeAngle(wheelRotation));
  return Math.floor(angleAtTopOnWheel / SECTION_ANGLE) % SECTIONS.length;
};

const getNextClockwiseRotation = (currentRotation, targetSectionIndex) => {
  const currentAngle = normalizeAngle(currentRotation);
  const targetAngle = getRotationForSectionAtTop(targetSectionIndex);
  const deltaToTarget = normalizeAngle(targetAngle - currentAngle);

  return currentRotation + FULL_SPINS * 360 + deltaToTarget;
};

const buildEligibilityMessage = (progress, isLoading, error) => {
  if (isLoading) {
    return 'Checking your spin status...';
  }

  if (error) {
    return error;
  }

  if (isSpinWheelEligible(progress)) {
    const spinCount = progress?.spins_available || 1;

    return `You have ${spinCount} spin${spinCount === 1 ? '' : 's'} available.`;
  }

  const remainingVisits = getRemainingOpenDays(progress);

  return `You need to visit the app ${remainingVisits} more time${
    remainingVisits === 1 ? '' : 's'
  } on different days before you can spin again.`;
};

const getFruitResultSubtitle = (resultDetails) => {
  if (resultDetails?.isDuplicate) {
    return 'You already have this fruit';
  }

  if (resultDetails?.collectionComplete) {
    return 'You collected all fruits';
  }

  return 'You have collected a fruit';
};

function FruitCollectionResult({ collectedFruits, onClose, resultDetails, selectedResult }) {
  const selectedResultImage =
    resultDetails?.collectionComplete && !resultDetails?.isDuplicate
      ? fullBasketGif
      : (FRUIT_RESULT_GIFS[selectedResult.name] ?? selectedResult.image);
  const fruitCount = collectedFruits.length;
  const fruitCountLabel = `${fruitCount} Fruit${fruitCount === 1 ? '' : 's'} Collected`;
  const collectionSlots = FRUIT_NAMES.map((slotName, index) => ({
    slotName,
    fruitName: collectedFruits[index] || null,
  }));

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (resultDetails?.showRewardScreen) {
    return (
      <div className="spin-wheel__modal-overlay" onClick={handleOverlayClick}>
        <div role="dialog" aria-modal="true" className="spin-wheel__reward-result" aria-label="1 GB collected">
          <button type="button" className="spin-wheel__close-btn" onClick={onClose}>
            x
          </button>
          <img src={oneGBCollectedScreen} alt="1 GB collected" className="spin-wheel__reward-screen-image" />
        </div>
      </div>
    );
  }

  const title = resultDetails?.isDuplicate ? 'ALREADY COLLECTED' : 'CONGRATULATIONS';
  const subtitle = getFruitResultSubtitle(resultDetails);

  return (
    <div className="spin-wheel__modal-overlay" onClick={handleOverlayClick}>
      <div role="dialog" aria-modal="true" className="spin-wheel__fruit-result" aria-label="Fruit collection result">
        <button type="button" className="spin-wheel__close-btn spin-wheel__fruit-close-btn" onClick={onClose}>
          x
        </button>
        <div className="spin-wheel__fruit-hero">
          <h2>{title}</h2>
          <p>{subtitle}</p>
          <img src={selectedResultImage} alt={`${selectedResult.name} collected`} className="spin-wheel__fruit-gif" />
          {resultDetails?.isDuplicate && <p className="spin-wheel__fruit-note">You can spin again next time.</p>}
        </div>
        <div className="spin-wheel__fruit-slots" aria-label={fruitCountLabel}>
          {collectionSlots.map(({ slotName, fruitName }) => (
            <div
              key={slotName}
              className="spin-wheel__fruit-slot"
              aria-label={fruitName ? `${fruitName} collected` : 'Empty fruit slot'}
            >
              {fruitName && (
                <img src={FRUIT_IMAGES_BY_NAME[fruitName]} alt={fruitName} className="spin-wheel__fruit-slot-image" />
              )}
            </div>
          ))}
        </div>
        <p className="spin-wheel__fruit-count">{fruitCountLabel}</p>
      </div>
    </div>
  );
}

function SpinWheelComingSoon() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="spin-wheel-page">
      <div
        className="spin-wheel__coming-soon-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spin-wheel-coming-soon-title"
      >
        <div className="spin-wheel__coming-soon-modal">
          <img src={wheelImage} alt="" className="spin-wheel__coming-soon-image" />
          <h2 id="spin-wheel-coming-soon-title">{t('staticPages.spinWheelComingSoon.title')}</h2>
          <p>{t('staticPages.spinWheelComingSoon.description')}</p>
          <button type="button" className="spin-wheel__coming-soon-button" onClick={() => navigate('/homepage')}>
            {t('staticPages.spinWheelComingSoon.backToHome')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SpinWheelFeature() {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [selectedSection, setSelectedSection] = useState(null);
  const [resultDetails, setResultDetails] = useState(null);
  const [spinProgress, setSpinProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState('');
  const rotationRef = useRef(0);
  const timeoutRef = useRef(null);
  const rewardTimeoutRef = useRef(null);

  const clearRewardTimeout = () => {
    if (rewardTimeoutRef.current) {
      clearTimeout(rewardTimeoutRef.current);
      rewardTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeSpinProgress = async () => {
      setProgressLoading(true);
      setProgressError('');

      try {
        const sessionId = getOrCreateSessionId();
        const progress = await loadProgressRecord(sessionId);

        if (isMounted) {
          setSpinProgress(progress);
        }
      } catch (error) {
        if (isMounted) {
          setSpinProgress(null);
          setProgressError(SPIN_PROGRESS_UNAVAILABLE_MESSAGE);
        }

        // eslint-disable-next-line no-console
        console.error('Error loading spin wheel progress:', error);
      } finally {
        if (isMounted) {
          setProgressLoading(false);
        }
      }
    };

    initializeSpinProgress();

    return () => {
      isMounted = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      clearRewardTimeout();
    };
  }, []);

  const handleSpin = async () => {
    if (spinning || progressLoading || progressError) return;

    if (!isSpinWheelEligible(spinProgress)) {
      return;
    }

    setSpinning(true);
    setSelectedSection(null);
    setResultDetails(null);
    setProgressError('');
    clearRewardTimeout();

    const previousProgress = spinProgress;
    const consumedProgress = consumeSpinWheelProgress(spinProgress);

    setSpinProgress(consumedProgress);

    try {
      const savedProgress = await saveProgressRecord(consumedProgress);
      setSpinProgress(savedProgress);
    } catch (error) {
      setSpinProgress(previousProgress);
      setSpinning(false);
      setProgressError('We could not save your spin. Please try again.');

      // eslint-disable-next-line no-console
      console.error('Error saving spin wheel progress:', error);
      return;
    }

    const targetSectionIndexes = getTargetSectionIndexes(getValidCollectedFruits(spinProgress?.collected_fruits));
    const targetSectionIndex = targetSectionIndexes[Math.floor(Math.random() * targetSectionIndexes.length)];
    const finalRotation = getNextClockwiseRotation(rotationRef.current, targetSectionIndex);

    rotationRef.current = finalRotation;
    setRotation(finalRotation);

    timeoutRef.current = setTimeout(() => {
      const selectedIdx = getSectionIndexAtTop(finalRotation);
      const selectedResult = SECTIONS[selectedIdx];
      let nextResultDetails = null;

      if (selectedResult.type === 'fruit') {
        const previousCollectedFruits = getValidCollectedFruits(consumedProgress.collected_fruits);
        const isDuplicate = previousCollectedFruits.includes(selectedResult.name);
        const nextCollectedFruits = isDuplicate
          ? previousCollectedFruits
          : [...previousCollectedFruits, selectedResult.name];
        const collectionComplete = hasCollectedAllFruits(nextCollectedFruits);
        const nextProgress = normalizeSpinWheelProgress(
          {
            ...consumedProgress,
            collected_fruits: nextCollectedFruits,
          },
          consumedProgress.session_id
        );

        nextResultDetails = {
          collectionComplete,
          isDuplicate,
          showRewardScreen: false,
        };

        if (!isDuplicate) {
          setSpinProgress(nextProgress);
          saveProgressRecord(nextProgress).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Error saving collected fruit:', error);
          });
        }

        if (collectionComplete && !isDuplicate) {
          rewardTimeoutRef.current = setTimeout(() => {
            setResultDetails((currentDetails) =>
              currentDetails?.collectionComplete && !currentDetails?.isDuplicate
                ? { ...currentDetails, showRewardScreen: true }
                : currentDetails
            );
            rewardTimeoutRef.current = null;
          }, FULL_BASKET_REWARD_DELAY_MS);
        }
      }

      setSpinning(false);
      setResultDetails(nextResultDetails);
      setSelectedSection(selectedIdx);
      timeoutRef.current = null;
    }, SPIN_DURATION_MS);
  };

  const handleCloseModal = () => {
    clearRewardTimeout();
    setSelectedSection(null);
    setResultDetails(null);
  };

  const handleTryAgain = () => {
    setSelectedSection(null);
    handleSpin();
  };

  const handleCollectReward = () => {
    setSelectedSection(null);
  };

  const selectedResult = selectedSection !== null ? SECTIONS[selectedSection] : null;
  const collectedFruits = getValidCollectedFruits(spinProgress?.collected_fruits);
  const selectedResultImage = selectedResult?.resultImage ?? selectedResult?.image;
  const resultImageClassName = selectedResult?.resultImage
    ? 'spin-wheel__result-image spin-wheel__result-image--screen'
    : 'spin-wheel__result-image';
  const spinEligible = isSpinWheelEligible(spinProgress);
  const statusMessage = buildEligibilityMessage(spinProgress, progressLoading, progressError);
  const spinButtonDisabled = spinning || progressLoading || !spinEligible || Boolean(progressError);

  return (
    <div className="spin-wheel-page">
      <h1>Spin the Wheel</h1>
      <div className="spin-wheel__arrow-container">
        <div className="spin-wheel__arrow" />
      </div>
      <div className="spin-wheel__preview">
        <img
          src={wheelImage}
          alt="Spin the Wheel"
          className="spin-wheel__image"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? `transform ${SPIN_DURATION_MS}ms ${SPIN_EASING}` : 'none',
          }}
        />
      </div>
      <button type="button" className="spin-wheel__button" onClick={handleSpin} disabled={spinButtonDisabled}>
        {spinning ? 'Spinning...' : 'Spin the wheel'}
      </button>
      <p className="spin-wheel__eligibility-message">{statusMessage}</p>

      {selectedResult?.type === 'fruit' && (
        <FruitCollectionResult
          collectedFruits={collectedFruits}
          onClose={handleCloseModal}
          resultDetails={resultDetails}
          selectedResult={selectedResult}
        />
      )}

      {selectedResult && selectedResult.type !== 'fruit' && (
        <div className="spin-wheel__modal-overlay" onClick={handleCloseModal}>
          <div
            className={`spin-wheel__modal spin-wheel__modal--${selectedResult.type}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="spin-wheel__close-btn" onClick={handleCloseModal}>
              x
            </button>
            <div className="spin-wheel__modal-content">
              <h2>{selectedResult.name}</h2>
              <img src={selectedResultImage} alt={selectedResult.name} className={resultImageClassName} />
              {selectedResult.type === 'reward' && (
                <button type="button" className="spin-wheel__result-action" onClick={handleCollectReward}>
                  COLLECT REWARD
                </button>
              )}
              {selectedResult.type === 'try-again' && (
                <>
                  {!spinEligible && <p className="spin-wheel__result-message">{statusMessage}</p>}
                  <button
                    type="button"
                    className="spin-wheel__result-action"
                    onClick={handleTryAgain}
                    disabled={!spinEligible || spinning}
                  >
                    {spinEligible ? 'TRY AGAIN' : 'COME BACK LATER'}
                  </button>
                </>
              )}
              {selectedResult.type !== 'try-again' && !spinEligible && (
                <p className="spin-wheel__result-message">{statusMessage}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SpinWheel() {
  if (SPIN_WHEEL_COMING_SOON) {
    return <SpinWheelComingSoon />;
  }

  return <SpinWheelFeature />;
}
